// Buyer Approval Queue Page - Buyer Approval Orchestrator (PRD-BAO-001)
// Table-based layout like Engineering Review with supplier info and buyer-specific columns
// Final approval queue (post-engineering) for commercial approval decisions
import React, { useState } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
  Skeleton,
  Alert,
  Tooltip,
  IconButton,
  Pagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Rating,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  ShoppingCart as BuyerIcon,
  FilterListOff as ResetIcon,
  AccessTime as TimeIcon,
  TrendingDown as SavingsIcon,
  LocationOn as LocationIcon,
  Verified as VerifiedIcon,
  Inventory as InventoryIcon,
  CheckOutlined as ApproveIcon,
  CloseOutlined as RejectIcon,
  DescriptionOutlined as QuoteRequestIcon,
  RemoveRedEyeOutlined as ViewIcon,
  MoreVertOutlined as MoreIcon,
  Engineering as EngineeringIcon,
  Handshake as NegotiateIcon,
  KeyboardDoubleArrowUp as EmergencyIcon,
  KeyboardArrowUp as HighIcon,
  Remove as StandardIcon,
  KeyboardArrowDown as LowIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { formatDistanceToNow, differenceInHours } from 'date-fns';
import {
  useBuyerApprovalQueue,
  useBuyerDashboard,
} from '../../hooks/useBuyerReviews';
import type {
  BuyerReview,
  BuyerReviewStatus,
  ReviewPriority,
} from '../../services/buyerReviewService';
import { useBreadcrumbUpdater } from '@ciea.ai/smart-infra-navigation';

// Professional color palette
const colors = {
  primary: '#0f766e',
  primaryLight: '#14b8a6',
  success: '#059669',
  successLight: '#d1fae5',
  warning: '#d97706',
  warningLight: '#fef3c7',
  danger: '#dc2626',
  dangerLight: '#fee2e2',
  info: '#0369a1',
  infoLight: '#e0f2fe',
  purple: '#7c3aed',
  purpleLight: '#ede9fe',
  slate: {
    50: '#f8fafc',
    100: '#f1f5f9',
    200: '#e2e8f0',
    300: '#cbd5e1',
    400: '#94a3b8',
    500: '#64748b',
    600: '#475569',
    700: '#334155',
    800: '#1e293b',
    900: '#0f172a',
  },
};

const getMatchTypeStyle = (matchType?: string) => {
  switch (matchType) {
    case 'EXACT_MATCH':
      return { bg: colors.successLight, color: colors.success, label: 'EXACT MATCH' };
    case 'FORM_FIT_FUNCTION':
    case 'FFF_EQUIVALENT':
      return { bg: colors.infoLight, color: colors.info, label: 'FORM FIT FUNCTION' };
    case 'FUNCTIONAL_ONLY':
      return { bg: colors.warningLight, color: colors.warning, label: 'FUNCTIONAL ONLY' };
    default:
      return { bg: colors.slate[100], color: colors.slate[600], label: matchType?.replace(/_/g, ' ') || 'UNKNOWN' };
  }
};

const getPriorityStyle = (priority: ReviewPriority) => {
  switch (priority) {
    case 'EMERGENCY':
      return { bg: colors.dangerLight, color: colors.danger };
    case 'CRITICAL':
      return { bg: colors.dangerLight, color: colors.danger };
    case 'HIGH':
      return { bg: colors.warningLight, color: colors.warning };
    case 'STANDARD':
      return { bg: colors.slate[100], color: colors.slate[600] };
    case 'LOW':
      return { bg: colors.slate[100], color: colors.slate[500] };
    default:
      return { bg: colors.slate[100], color: colors.slate[600] };
  }
};

const getStatusStyle = (status: BuyerReviewStatus) => {
  switch (status) {
    case 'PENDING':
      return { bg: colors.slate[100], color: colors.slate[600], label: 'PENDING' };
    case 'IN_PROGRESS':
      return { bg: colors.infoLight, color: colors.info, label: 'IN PROGRESS' };
    case 'AWAITING_NEGOTIATION':
      return { bg: colors.purpleLight, color: colors.purple, label: 'NEGOTIATING' };
    case 'AWAITING_ONBOARDING':
      return { bg: colors.warningLight, color: colors.warning, label: 'ONBOARDING' };
    case 'ON_HOLD':
      return { bg: colors.slate[200], color: colors.slate[600], label: 'ON HOLD' };
    case 'COMPLETED':
      return { bg: colors.successLight, color: colors.success, label: 'COMPLETED' };
    case 'CANCELLED':
      return { bg: colors.dangerLight, color: colors.danger, label: 'CANCELLED' };
    default:
      return { bg: colors.slate[100], color: colors.slate[600], label: status.replace(/_/g, ' ') };
  }
};

const getEngineeringDecisionStyle = (decision?: string) => {
  switch (decision) {
    case 'APPROVE':
    case 'APPROVED':
      return { bg: colors.successLight, color: colors.success, label: 'APPROVED' };
    case 'APPROVE_WITH_CONDITIONS':
      return { bg: colors.warningLight, color: colors.warning, label: 'W/ CONDITIONS' };
    case 'REJECT':
    case 'REJECTED':
      return { bg: colors.dangerLight, color: colors.danger, label: 'REJECTED' };
    default:
      return { bg: colors.slate[100], color: colors.slate[500], label: 'PENDING' };
  }
};

const getConfidenceColor = (score: number) => {
  if (score >= 90) return colors.success;
  if (score >= 70) return colors.info;
  if (score >= 50) return colors.warning;
  return colors.danger;
};

const BuyerApprovalQueuePage: React.FC = () => {
  useBreadcrumbUpdater([{ path: '/buyer', breadcrumb: 'Buyer Review' }, { path: '/buyer/approvals', breadcrumb: 'Approvals' }]);
  const navigate = useNavigate();

  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<string[]>(['PENDING', 'IN_PROGRESS', 'AWAITING_NEGOTIATION']);
  const [priorityFilter, setPriorityFilter] = useState<string[]>([]);
  const [slaFilter, setSlaFilter] = useState<string>('all');

  const { data: queueData, isLoading, error, refetch } = useBuyerApprovalQueue({
    status: statusFilter.length > 0 ? statusFilter.join(',') : undefined,
    priority: priorityFilter.length > 0 ? priorityFilter.join(',') : undefined,
    slaBreached: slaFilter === 'breached' ? true : slaFilter === 'healthy' ? false : undefined,
    page,
    limit: 20,
  });

  const { data: dashboard } = useBuyerDashboard();

  const getSLAInfo = (review: BuyerReview) => {
    if (review.status === 'COMPLETED' || review.status === 'CANCELLED') {
      return { label: '-', color: colors.slate[500], urgent: false, icon: false };
    }

    if (review.slaBreached) {
      return { label: 'Overdue', color: colors.danger, urgent: true, icon: true };
    }

    const dueDate = new Date(review.dueAt);
    const hoursRemaining = differenceInHours(dueDate, new Date());
    const daysRemaining = Math.ceil(hoursRemaining / 24);

    if (daysRemaining <= 0) {
      return { label: 'Today', color: colors.danger, urgent: true, icon: true };
    }
    if (daysRemaining === 1) {
      return { label: '1 day', color: colors.warning, urgent: true, icon: true };
    }
    if (daysRemaining <= 3) {
      return { label: `${daysRemaining} days`, color: colors.warning, urgent: false, icon: false };
    }

    return { label: `${daysRemaining} days`, color: colors.slate[500], urgent: false, icon: false };
  };

  const StatCard = ({
    label,
    value,
    icon,
    active,
    onClick,
    subValue,
  }: {
    label: string;
    value: number | string;
    icon: React.ReactNode;
    active?: boolean;
    onClick?: () => void;
    subValue?: string;
  }) => (
    <Paper
      elevation={0}
      onClick={onClick}
      sx={{
        p: 2.5,
        cursor: onClick ? 'pointer' : 'default',
        border: `1px solid ${active ? colors.primary : colors.slate[200]}`,
        backgroundColor: active ? '#f0fdfa' : 'white',
        borderRadius: 2,
        transition: 'all 0.15s ease',
        '&:hover': onClick
          ? {
              borderColor: colors.primary,
              backgroundColor: '#f0fdfa',
            }
          : {},
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 600, color: colors.slate[800], lineHeight: 1.2 }}>
            {value}
          </Typography>
          <Typography variant="body2" sx={{ color: colors.slate[500], mt: 0.5, fontWeight: 500 }}>
            {label}
          </Typography>
          {subValue && (
            <Typography variant="caption" sx={{ color: colors.success, fontWeight: 600 }}>
              {subValue}
            </Typography>
          )}
        </Box>
        <Box sx={{ color: active ? colors.primary : colors.slate[400] }}>{icon}</Box>
      </Stack>
    </Paper>
  );

  const resetFilters = () => {
    setStatusFilter(['PENDING', 'IN_PROGRESS', 'AWAITING_NEGOTIATION']);
    setPriorityFilter([]);
    setSlaFilter('all');
    setPage(1);
  };

  // Supplier Info Cell Component
  const SupplierInfoCell = ({ review }: { review: BuyerReview }) => {
    const rating = (review.supplierScorecard?.overallScore || review.confidenceScore || 85) / 20;
    const ratingPercent = review.supplierScorecard?.overallScore || review.confidenceScore || 85;

    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 200 }}>
        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: 1.5,
            backgroundColor: colors.slate[100],
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: `1px solid ${colors.slate[200]}`,
          }}
        >
          <InventoryIcon sx={{ color: colors.slate[500], fontSize: 22 }} />
        </Box>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, color: colors.slate[800] }}>
              {review.supplierName || 'Unknown Supplier'}
            </Typography>
            <VerifiedIcon sx={{ fontSize: 14, color: colors.primary }} />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <LocationIcon sx={{ fontSize: 12, color: colors.slate[400] }} />
            <Typography variant="caption" sx={{ color: colors.slate[500] }}>
              Global
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Rating value={rating} precision={0.5} size="small" readOnly sx={{ fontSize: 12 }} />
            <Typography variant="caption" sx={{ color: colors.slate[500] }}>
              ({ratingPercent}%)
            </Typography>
          </Box>
        </Box>
      </Box>
    );
  };

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Failed to load approval queue. Please try again.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, backgroundColor: colors.slate[50], minHeight: '100vh' }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 600, color: colors.slate[800] }}>
            Final Approval Queue
          </Typography>
          <Typography variant="body2" sx={{ color: colors.slate[500], mt: 0.5 }}>
            Post-engineering commercial approval decisions
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Tooltip title="Refresh">
            <IconButton onClick={() => refetch()} size="small">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Button
            variant="outlined"
            size="small"
            startIcon={<ResetIcon />}
            onClick={resetFilters}
            sx={{ borderColor: colors.slate[300], color: colors.slate[600] }}
          >
            Reset Filters
          </Button>
        </Stack>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            label="Awaiting Approval"
            value={dashboard?.approvalQueue?.pending || 0}
            icon={<BuyerIcon sx={{ fontSize: 32 }} />}
            active={statusFilter.includes('PENDING') && statusFilter.length === 1}
            onClick={() => {
              setStatusFilter(['PENDING']);
              setPage(1);
            }}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            label="In Negotiation"
            value={dashboard?.approvalQueue?.awaitingNegotiation || 0}
            icon={<NegotiateIcon sx={{ fontSize: 32 }} />}
            active={statusFilter.includes('AWAITING_NEGOTIATION') && statusFilter.length === 1}
            onClick={() => {
              setStatusFilter(['AWAITING_NEGOTIATION']);
              setPage(1);
            }}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            label="SLA At Risk"
            value={dashboard?.atRisk || 0}
            icon={<TimeIcon sx={{ fontSize: 32 }} />}
            active={slaFilter === 'breached'}
            onClick={() => {
              setSlaFilter('breached');
              setPage(1);
            }}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            label="Total Savings"
            value={`$${((dashboard?.totalPotentialSavings || 0) / 1000).toFixed(0)}K`}
            icon={<SavingsIcon sx={{ fontSize: 32 }} />}
            subValue={`${dashboard?.avgSavingsPercentage?.toFixed(0) || 0}% avg savings`}
          />
        </Grid>
      </Grid>

      {/* Filters */}
      <Paper elevation={0} sx={{ p: 2, mb: 3, border: `1px solid ${colors.slate[200]}`, borderRadius: 2 }}>
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Status</InputLabel>
            <Select
              multiple
              value={statusFilter}
              label="Status"
              onChange={(e) => {
                setStatusFilter(e.target.value as string[]);
                setPage(1);
              }}
              renderValue={(selected) => `${selected.length} selected`}
            >
              <MenuItem value="PENDING">Pending</MenuItem>
              <MenuItem value="IN_PROGRESS">In Progress</MenuItem>
              <MenuItem value="AWAITING_NEGOTIATION">Awaiting Negotiation</MenuItem>
              <MenuItem value="AWAITING_ONBOARDING">Awaiting Onboarding</MenuItem>
              <MenuItem value="ON_HOLD">On Hold</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Priority</InputLabel>
            <Select
              multiple
              value={priorityFilter}
              label="Priority"
              onChange={(e) => {
                setPriorityFilter(e.target.value as string[]);
                setPage(1);
              }}
              renderValue={(selected) => (selected.length > 0 ? `${selected.length} selected` : 'All')}
            >
              <MenuItem value="EMERGENCY">Emergency</MenuItem>
              <MenuItem value="CRITICAL">Critical</MenuItem>
              <MenuItem value="HIGH">High</MenuItem>
              <MenuItem value="STANDARD">Standard</MenuItem>
              <MenuItem value="LOW">Low</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>SLA Status</InputLabel>
            <Select
              value={slaFilter}
              label="SLA Status"
              onChange={(e) => {
                setSlaFilter(e.target.value);
                setPage(1);
              }}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="healthy">Healthy</MenuItem>
              <MenuItem value="breached">Breached</MenuItem>
            </Select>
          </FormControl>
        </Stack>
      </Paper>

      {/* Table */}
      <Paper elevation={0} sx={{ border: `1px solid ${colors.slate[200]}`, borderRadius: 2, overflow: 'hidden', backgroundColor: colors.slate[100] }}>
        {isLoading && <LinearProgress />}
        <TableContainer>
          <Table sx={{ borderCollapse: 'separate', borderSpacing: '0 8px' }}>
            <TableHead>
              <TableRow sx={{ backgroundColor: 'transparent' }}>
                <TableCell sx={{ fontWeight: 600, color: colors.slate[600], textTransform: 'uppercase', fontSize: '0.75rem' }}>
                  Supplier
                </TableCell>
                <TableCell sx={{ fontWeight: 600, color: colors.slate[600], textTransform: 'uppercase', fontSize: '0.75rem' }}>
                  Master Part
                </TableCell>
                <TableCell sx={{ fontWeight: 600, color: colors.slate[600], textTransform: 'uppercase', fontSize: '0.75rem' }}>
                  Alternative Part
                </TableCell>
                <TableCell sx={{ fontWeight: 600, color: colors.slate[600], textTransform: 'uppercase', fontSize: '0.75rem' }}>
                  Review #
                </TableCell>
                <TableCell sx={{ fontWeight: 600, color: colors.slate[600], textTransform: 'uppercase', fontSize: '0.75rem', width: 40 }}>
                  Pri
                </TableCell>
                <TableCell sx={{ fontWeight: 600, color: colors.slate[600], textTransform: 'uppercase', fontSize: '0.75rem' }}>
                  Match Type
                </TableCell>
                <TableCell sx={{ fontWeight: 600, color: colors.slate[600], textTransform: 'uppercase', fontSize: '0.75rem' }}>
                  Engineering
                </TableCell>
                <TableCell sx={{ fontWeight: 600, color: colors.slate[600], textTransform: 'uppercase', fontSize: '0.75rem' }}>
                  Unit Price
                </TableCell>
                <TableCell sx={{ fontWeight: 600, color: colors.slate[600], textTransform: 'uppercase', fontSize: '0.75rem' }}>
                  Price Diff
                </TableCell>
                <TableCell sx={{ fontWeight: 600, color: colors.slate[600], textTransform: 'uppercase', fontSize: '0.75rem' }}>
                  Savings
                </TableCell>
                <TableCell sx={{ fontWeight: 600, color: colors.slate[600], textTransform: 'uppercase', fontSize: '0.75rem' }}>
                  Status
                </TableCell>
                <TableCell sx={{ fontWeight: 600, color: colors.slate[600], textTransform: 'uppercase', fontSize: '0.75rem' }}>
                  SLA
                </TableCell>
                <TableCell sx={{ fontWeight: 600, color: colors.slate[600], textTransform: 'uppercase', fontSize: '0.75rem' }}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                Array.from({ length: 5 }).map((_, idx) => (
                  <TableRow key={idx}>
                    {Array.from({ length: 13 }).map((_, cellIdx) => (
                      <TableCell key={cellIdx}>
                        <Skeleton variant="text" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : queueData?.data?.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={13} sx={{ textAlign: 'center', py: 4 }}>
                    <Typography color="textSecondary">No reviews in approval queue</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                queueData?.data?.map((review) => {
                  const slaInfo = getSLAInfo(review);
                  const matchStyle = getMatchTypeStyle(review.matchType);
                  const priorityStyle = getPriorityStyle(review.priority);
                  const statusStyle = getStatusStyle(review.status);
                  const engineeringStyle = getEngineeringDecisionStyle(review.engineeringDecision);

                  // Calculate price diff and savings
                  const originalPrice = review.savingsCalculation?.originalUnitPrice || 0;
                  const altPrice = review.savingsCalculation?.alternativeUnitPrice || 0;
                  const priceDiff = originalPrice > 0 ? originalPrice - altPrice : 0;
                  const priceDiffPercent = originalPrice > 0 ? ((priceDiff / originalPrice) * 100) : 0;
                  const annualSavings = review.savingsCalculation?.annualSavings || 0;

                  return (
                    <TableRow
                      key={review._id}
                      hover
                      sx={{
                        cursor: 'pointer',
                        backgroundColor: 'white',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                        '&:hover': { backgroundColor: colors.slate[50] },
                        '& td:first-of-type': {
                          borderTopLeftRadius: 8,
                          borderBottomLeftRadius: 8,
                          borderLeft: `3px solid ${
                            review.priority === 'EMERGENCY' || review.priority === 'CRITICAL' ? colors.danger :
                            review.priority === 'HIGH' ? colors.warning :
                            review.priority === 'STANDARD' ? '#facc15' :
                            colors.slate[300]
                          }`,
                        },
                        '& td:last-of-type': {
                          borderTopRightRadius: 8,
                          borderBottomRightRadius: 8,
                        },
                      }}
                      onClick={() => navigate(`/buyer/reviews/${review._id}`)}
                    >
                      {/* Supplier Info */}
                      <TableCell sx={{ py: 2 }}>
                        <SupplierInfoCell review={review} />
                      </TableCell>

                      {/* Master Part */}
                      <TableCell>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: colors.slate[800] }}>
                            {review.sourcePartNumber || '-'}
                          </Typography>
                          <Typography variant="caption" sx={{ color: colors.primary }}>
                            {review.masterManufacturer || 'Unknown'}
                          </Typography>
                        </Box>
                      </TableCell>

                      {/* Alternative Part */}
                      <TableCell>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 500, color: colors.slate[800] }}>
                            {review.alternativePartNumber}
                          </Typography>
                          <Typography variant="caption" sx={{ color: colors.primary }}>
                            {review.alternativeManufacturer}
                          </Typography>
                        </Box>
                      </TableCell>

                      {/* Review # */}
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: colors.slate[600] }}>
                          {review.reviewNumber}
                        </Typography>
                      </TableCell>

                      {/* Priority Icon */}
                      <TableCell>
                        <Tooltip title={review.priority}>
                          {review.priority === 'EMERGENCY' || review.priority === 'CRITICAL' ? (
                            <EmergencyIcon sx={{ fontSize: 18, color: colors.danger }} />
                          ) : review.priority === 'HIGH' ? (
                            <HighIcon sx={{ fontSize: 18, color: colors.warning }} />
                          ) : review.priority === 'LOW' ? (
                            <LowIcon sx={{ fontSize: 18, color: colors.slate[400] }} />
                          ) : (
                            <StandardIcon sx={{ fontSize: 18, color: colors.slate[400] }} />
                          )}
                        </Tooltip>
                      </TableCell>

                      {/* Match Type */}
                      <TableCell>
                        <Chip
                          label={matchStyle.label}
                          size="small"
                          sx={{
                            backgroundColor: matchStyle.bg,
                            color: matchStyle.color,
                            fontWeight: 600,
                            fontSize: '0.65rem',
                            height: 22,
                          }}
                        />
                      </TableCell>

                      {/* Engineering Decision */}
                      <TableCell>
                        <Chip
                          icon={<EngineeringIcon sx={{ fontSize: 12 }} />}
                          label={engineeringStyle.label}
                          size="small"
                          sx={{
                            backgroundColor: engineeringStyle.bg,
                            color: engineeringStyle.color,
                            fontWeight: 600,
                            fontSize: '0.65rem',
                            height: 22,
                            '& .MuiChip-icon': { color: engineeringStyle.color },
                          }}
                        />
                      </TableCell>

                      {/* Unit Price */}
                      <TableCell>
                        {altPrice > 0 ? (
                          <Typography variant="body2" sx={{ fontWeight: 600, color: colors.slate[800] }}>
                            ${altPrice.toFixed(2)}
                          </Typography>
                        ) : (
                          <Typography variant="body2" sx={{ color: colors.slate[400] }}>
                            -
                          </Typography>
                        )}
                      </TableCell>

                      {/* Price Diff */}
                      <TableCell>
                        {priceDiff !== 0 ? (
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 600,
                              color: priceDiff > 0 ? colors.success : colors.danger,
                            }}
                          >
                            {priceDiff > 0 ? '-' : '+'}${Math.abs(priceDiff).toFixed(2)} ({Math.abs(priceDiffPercent).toFixed(0)}%)
                          </Typography>
                        ) : (
                          <Typography variant="body2" sx={{ color: colors.slate[400] }}>
                            -
                          </Typography>
                        )}
                      </TableCell>

                      {/* Savings */}
                      <TableCell>
                        {annualSavings > 0 ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <SavingsIcon sx={{ fontSize: 14, color: colors.success }} />
                            <Typography variant="body2" sx={{ fontWeight: 600, color: colors.success }}>
                              ${annualSavings.toLocaleString()}
                            </Typography>
                          </Box>
                        ) : (
                          <Typography variant="body2" sx={{ color: colors.slate[400] }}>
                            -
                          </Typography>
                        )}
                      </TableCell>

                      {/* Status */}
                      <TableCell>
                        <Chip
                          size="small"
                          label={statusStyle.label}
                          sx={{
                            backgroundColor: colors.slate[100],
                            color: colors.slate[700],
                            fontWeight: 500,
                            fontSize: '0.7rem',
                            height: 22,
                            border: `1px solid ${colors.slate[200]}`,
                            '& .MuiChip-label': { px: 1 },
                          }}
                        />
                      </TableCell>

                      {/* SLA */}
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          {slaInfo.icon && (
                            <TimeIcon sx={{ fontSize: 14, color: slaInfo.color }} />
                          )}
                          <Typography
                            variant="body2"
                            sx={{
                              color: slaInfo.color,
                              fontWeight: slaInfo.urgent ? 600 : 400,
                            }}
                          >
                            {slaInfo.label}
                          </Typography>
                        </Box>
                      </TableCell>

                      {/* Actions */}
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <Stack direction="row" spacing={0.5}>
                          <Tooltip title="Approve">
                            <IconButton
                              size="small"
                              sx={{
                                color: colors.slate[600],
                                '&:hover': { backgroundColor: colors.slate[100] },
                              }}
                            >
                              <ApproveIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Reject">
                            <IconButton
                              size="small"
                              sx={{
                                color: colors.slate[600],
                                '&:hover': { backgroundColor: colors.slate[100] },
                              }}
                            >
                              <RejectIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Request Quote">
                            <IconButton
                              size="small"
                              sx={{
                                color: colors.slate[600],
                                '&:hover': { backgroundColor: colors.slate[100] },
                              }}
                            >
                              <QuoteRequestIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="View Details">
                            <IconButton
                              size="small"
                              sx={{
                                color: colors.slate[600],
                                '&:hover': { backgroundColor: colors.slate[100] },
                              }}
                              onClick={() => navigate(`/buyer/reviews/${review._id}`)}
                            >
                              <ViewIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="More Actions">
                            <IconButton
                              size="small"
                              sx={{
                                color: colors.slate[600],
                                '&:hover': { backgroundColor: colors.slate[100] },
                              }}
                            >
                              <MoreIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        {queueData && queueData.totalPages > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2, borderTop: `1px solid ${colors.slate[200]}` }}>
            <Pagination
              count={queueData.totalPages}
              page={page}
              onChange={(_, newPage) => setPage(newPage)}
              color="primary"
            />
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default BuyerApprovalQueuePage;
